import {currentUser} from '@/services/FirestoreService';
import {getStore} from '@/services/store';

import {appCheckBackend, backend, ownPublicApi} from './client';

export type AuditLogQueryType = 'sharingHistory' | 'usageStatistics';

// What the insert route accepts; the backend adds who sent it, when, and from where.
export interface AuditRecord {
  eventType: string;
  eventPayload?: unknown;
  tilesFetched?: number | null;
  mapId?: string;
  mapName?: string | null;
  mapCreatorId?: string | null;
}

// Row columns depend on the query type.
export const queryAuditLogs = async <T = Record<string, unknown>>(
  mapId: string,
  queryType: AuditLogQueryType,
): Promise<T[]> => {
  return backend.post<T[]>('external-public-api', {mapId, queryType}, {path: 'api/audit-logs/query'});
};

// The open shared map's fields; Beyond Maps has none.
const openMapFields = (): Partial<AuditRecord> => {
  const sharedMap = getStore().state.sharedMap;
  if (!sharedMap) return {};
  return {mapId: sharedMap.id, mapName: sharedMap.name, mapCreatorId: sharedMap.createdOn?.uid};
};

// Throws on failure, for callers that act on it. Map fields come from the open shared map unless the
// record names its own. Employees send their ID token; everyone else (signed out, or signed in without
// the claim) sends App Check and is logged as anonymous. The forced refresh makes a just-granted claim count.
export const insertAuditRecord = async (record: AuditRecord): Promise<void> => {
  const token = await currentUser()?.getIdTokenResult(true);
  const client = token?.claims?.npEmployee ? backend : appCheckBackend;
  await client.post(ownPublicApi(), {...openMapFields(), ...record}, {path: 'api/audit-logs/insert'});
};

// Never throws: a failed audit is logged so it can't break the action that triggered it.
export const createAuditRecord = async (
  eventType: string,
  eventPayload: unknown = null,
  tilesFetched: number | null = null,
): Promise<void> => {
  try {
    await insertAuditRecord({eventType, eventPayload, tilesFetched});
  } catch (error) {
    console.error('Error creating audit record:', error);
  }
};
